import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

export function approvalTitle(memberName: string): string {
  return `Encryption key for ${memberName} changed`;
}

export function approvalBody(memberName: string): string {
  return `A new device or a reset identity published a new encryption key for ${memberName}. Approve only if you expect this change. Approving gives the new key access to this conversation's history.`;
}

export interface KeyChangeApprovalDialogProps {
  open: boolean;
  memberName: string;
  /** The pending key's fingerprint in 8 groups of 4. */
  fingerprint: string;
  busy?: boolean;
  error?: string | null;
  onApprove: () => void;
  onNotNow: () => void;
}

/**
 * One explicit approve click per (conversation, member, pending public key).
 * "Not now" suppresses that key until the conversation is next opened.
 */
export function KeyChangeApprovalDialog({
  open,
  memberName,
  fingerprint,
  busy = false,
  error,
  onApprove,
  onNotNow,
}: KeyChangeApprovalDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onNotNow}
      maxWidth="xs"
      fullWidth
      className="ph-no-capture" PaperProps={{ className: 'ph-no-capture' }}
      aria-labelledby="key-change-approval-title"
    >
      <DialogTitle id="key-change-approval-title" sx={{ fontWeight: 600 }}>
        {approvalTitle(memberName)}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <DialogContentText sx={{ fontSize: '0.875rem' }}>{approvalBody(memberName)}</DialogContentText>
        <Box
          aria-label="New key fingerprint"
          sx={{
            fontFamily: 'monospace',
            fontSize: 15,
            textAlign: 'center',
            p: 1.25,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {fingerprint}
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onNotNow} disabled={busy} sx={{ color: 'text.secondary' }}>
          Not now
        </Button>
        <Button variant="contained" onClick={onApprove} disabled={busy} sx={{ minWidth: 110 }}>
          {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
